/**
 * Inventories Page
 *
 * List page for inventories with filters, KPIs, and drawer.
 * Supports wizard modal for creating/editing inventories.
 * @see US-12.6 - Page Liste
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { ClipboardList, Package, Clock, AlertTriangle } from 'lucide-react';
import { Button, Skeleton, EmptyState, KPICard } from '@/components/ui';
import type { DateRange } from '@/components/ui';
import { useDrawerRoute, useCurrency, usePermission, useCanSeeFinancials } from '@/hooks';
import { formatDateParam } from '@/components/ui';
import { formatCentimes } from '@/lib/format';
import {
  useInventories,
  useInventoriesKPIs,
  useValidateInventory,
  useCancelInventory,
  useDeleteInventory,
} from '../hooks/useInventories';
import { inventoriesService } from '../services/inventories.service';
import { InventoriesFilters } from '../components/InventoriesFilters';
import { InventoriesTable } from '../components/InventoriesTable';
import { InventoryDrawer } from '../components/InventoryDrawer';
import { InventoryWizardModal } from '../components/InventoryWizardModal';
import { InventoryStatus } from '@bstock/shared';
import type { InventoryListItem, QueryInventoriesDto } from '@bstock/shared';

type SortField = QueryInventoriesDto['sortBy'];
type SortOrder = QueryInventoriesDto['sortOrder'];

const DEFAULT_LIMIT = 20;

/**
 * Get initial date range (current month)
 */
function getInitialDateRange(): DateRange {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

/**
 * Parse URL params into filters (deep linking)
 */
function parseUrlParams(searchParams: URLSearchParams): {
  status: string;
  type: string;
  search: string;
  dateRange: DateRange;
  page: number;
  sortBy: SortField;
  sortOrder: SortOrder;
} {
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const defaultRange = getInitialDateRange();

  return {
    status: searchParams.get('status') ?? '',
    type: searchParams.get('type') ?? '',
    search: searchParams.get('search') ?? '',
    dateRange: {
      start: startDate ? new Date(startDate) : defaultRange.start,
      end: endDate ? new Date(endDate) : defaultRange.end,
    },
    page: parseInt(searchParams.get('page') ?? '1', 10),
    sortBy: (searchParams.get('sortBy') as SortField) ?? 'createdAt',
    sortOrder: (searchParams.get('sortOrder') as SortOrder) ?? 'desc',
  };
}

export default function InventoriesPage() {
  const currency = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { inventoryId: urlInventoryId } = useParams<{ inventoryId?: string }>();

  // Permissions
  const canCreate = usePermission('inventories.create');
  const canValidate = usePermission('inventories.validate');
  const canDelete = usePermission('inventories.delete');
  const showFinancials = useCanSeeFinancials();

  // Parse URL params for deep linking
  const urlParams = useMemo(() => parseUrlParams(searchParams), [searchParams]);

  // Drawer routing with deep linking
  const drawer = useDrawerRoute('/stock/inventaires', { paramName: 'inventoryId', suffix: 'details' });

  // Create/edit modal state - detect routes
  const isCreateRoute = location.pathname.endsWith('/create');
  const isEditRoute = location.pathname.includes('/edit');
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [editingInventoryId, setEditingInventoryId] = useState<string | null>(null);

  // Open wizard when navigating to edit route via URL
  useEffect(() => {
    if (isEditRoute && urlInventoryId && !isWizardOpen) {
      setEditingInventoryId(urlInventoryId);
      setIsWizardOpen(true);
    }
  }, [isEditRoute, urlInventoryId, isWizardOpen]);

  // Filters state from URL
  const [statusFilter, setStatusFilter] = useState(urlParams.status);
  const [typeFilter, setTypeFilter] = useState(urlParams.type);
  const [dateRange, setDateRange] = useState<DateRange>(urlParams.dateRange);
  const [searchQuery, setSearchQuery] = useState(urlParams.search);
  const [page, setPage] = useState(urlParams.page);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [sortBy, setSortBy] = useState<SortField>(urlParams.sortBy);
  const [sortOrder, setSortOrder] = useState<SortOrder>(urlParams.sortOrder);

  // Update URL when filters change
  const updateUrl = useCallback(
    (updates: Record<string, string | null>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  // Query params
  const queryParams = useMemo(() => ({
    page,
    limit,
    status: statusFilter ? [statusFilter as InventoryStatus] : undefined,
    type: typeFilter || undefined,
    search: searchQuery || undefined,
    startDate: formatDateParam(dateRange.start) ?? undefined,
    endDate: formatDateParam(dateRange.end) ?? undefined,
    sortBy,
    sortOrder,
  }), [page, limit, statusFilter, typeFilter, searchQuery, dateRange, sortBy, sortOrder]);

  // Fetch inventories and KPIs
  const { data: inventoriesData, isLoading, refetch } = useInventories(queryParams as QueryInventoriesDto);
  const { data: kpis, refetch: refetchKpis } = useInventoriesKPIs(
    formatDateParam(dateRange.start) ?? undefined,
    formatDateParam(dateRange.end) ?? undefined
  );

  // Mutations
  const validateMutation = useValidateInventory();
  const cancelMutation = useCancelInventory();
  const deleteMutation = useDeleteInventory();

  const inventories = inventoriesData?.data ?? [];
  const pagination = inventoriesData?.pagination;

  // Modal handlers
  const handleOpenCreate = useCallback(() => {
    setEditingInventoryId(null);
    setIsWizardOpen(true);
    navigate('/stock/inventaires/create');
  }, [navigate]);

  const handleOpenEdit = useCallback((inventoryId: string) => {
    setEditingInventoryId(inventoryId);
    setIsWizardOpen(true);
    navigate(`/stock/inventaires/${inventoryId}/edit`);
  }, [navigate]);

  const handleCloseWizard = useCallback(() => {
    setIsWizardOpen(false);
    setEditingInventoryId(null);
    if (isCreateRoute || isEditRoute) {
      navigate('/stock/inventaires');
    }
  }, [isCreateRoute, isEditRoute, navigate]);

  const handleWizardSuccess = useCallback(() => {
    refetch();
    refetchKpis();
  }, [refetch, refetchKpis]);

  // Handlers
  const handleRowClick = useCallback(
    (inventory: InventoryListItem) => {
      drawer.open(inventory.id);
    },
    [drawer]
  );

  const handleContinue = useCallback(
    (inventory: InventoryListItem) => {
      handleOpenEdit(inventory.id);
    },
    [handleOpenEdit]
  );

  const handleValidate = useCallback(
    async (inventory: InventoryListItem) => {
      if (window.confirm(`Voulez-vous valider l'inventaire ${inventory.inventoryNumber} ?`)) {
        await validateMutation.mutateAsync(inventory.id);
      }
    },
    [validateMutation]
  );

  const handleCancel = useCallback(
    async (inventory: InventoryListItem) => {
      if (window.confirm(`Voulez-vous annuler l'inventaire ${inventory.inventoryNumber} ?`)) {
        await cancelMutation.mutateAsync({ id: inventory.id });
      }
    },
    [cancelMutation]
  );

  const handleDelete = useCallback(
    async (inventory: InventoryListItem) => {
      if (window.confirm(`Voulez-vous supprimer l'inventaire ${inventory.inventoryNumber} ?`)) {
        await deleteMutation.mutateAsync(inventory.id);
      }
    },
    [deleteMutation]
  );

  const handleDownloadSheet = useCallback(
    async (inventoryId: string, format: 'pdf' | 'excel') => {
      try {
        await inventoriesService.downloadSheet(inventoryId, format);
      } catch {
        // Error silently handled
      }
    },
    []
  );

  const handleSort = useCallback((field: SortField, order: SortOrder) => {
    setSortBy(field);
    setSortOrder(order);
    setPage(1);
    updateUrl({ sortBy: field || null, sortOrder: order || null, page: null });
  }, [updateUrl]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    updateUrl({ page: String(newPage) });
  }, [updateUrl]);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    updateUrl({ page: null });
  }, [updateUrl]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <ClipboardList className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-dark-500">Inventaires</h1>
              <p className="text-sm text-neutral-500 mt-0.5">
                Gerez vos inventaires physiques
              </p>
            </div>
          </div>
{canCreate && (
            <Button onClick={handleOpenCreate}>
              Nouvel inventaire
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4 flex-shrink-0">
        <div className={`grid grid-cols-2 ${showFinancials ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
          <KPICard
            title="Total inventaires"
            value={kpis?.totalInventories ?? 0}
            icon={<ClipboardList className="w-5 h-5" />}
          />
          <KPICard
            title="En cours"
            value={kpis?.inProgressInventories ?? 0}
            icon={<Clock className="w-5 h-5" />}
            badge={kpis && kpis.inProgressInventories > 0 ? { label: 'A completer', variant: 'warning' } : undefined}
          />
          {showFinancials && (
            <KPICard
              title="Ecart total"
              value={kpis ? formatCentimes(Math.abs(kpis.totalVarianceValue), currency) : '-'}
              icon={<Package className="w-5 h-5" />}
            />
          )}
          <KPICard
            title="Avec ecarts"
            value={kpis?.inventoriesWithVariance ?? 0}
            icon={<AlertTriangle className="w-5 h-5" />}
            badge={kpis && kpis.inventoriesWithVariance > 0 ? { label: 'Attention', variant: 'danger' } : undefined}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-neutral-200 px-6 py-3 flex-shrink-0">
        <InventoriesFilters
          status={statusFilter}
          type={typeFilter}
          searchQuery={searchQuery}
          dateRange={dateRange}
          onStatusChange={(v) => {
            setStatusFilter(v);
            setPage(1);
            updateUrl({ status: v || null, page: null });
          }}
          onTypeChange={(v) => {
            setTypeFilter(v);
            setPage(1);
            updateUrl({ type: v || null, page: null });
          }}
          onSearchChange={(v) => {
            setSearchQuery(v);
            setPage(1);
            updateUrl({ search: v || null, page: null });
          }}
          onDateRangeChange={(range) => {
            setDateRange(range);
            setPage(1);
            updateUrl({
              startDate: formatDateParam(range.start),
              endDate: formatDateParam(range.end),
              page: null,
            });
          }}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 bg-neutral-50">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-16" />
            ))}
          </div>
        ) : inventories.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title="Aucun inventaire trouve"
            description="Creez votre premier inventaire pour controler vos stocks"
            action={canCreate ? {
              label: 'Nouvel inventaire',
              onClick: handleOpenCreate,
            } : undefined}
          />
        ) : (
          <InventoriesTable
            inventories={inventories}
            pagination={pagination ?? { page: 1, limit, total: 0, totalPages: 0 }}
            isLoading={isLoading}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onRowClick={handleRowClick}
            onContinue={handleContinue}
            onValidate={canValidate ? handleValidate : undefined}
            onCancel={canValidate ? handleCancel : undefined}
            onDelete={canDelete ? handleDelete : undefined}
            onDownloadSheet={handleDownloadSheet}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
            showFinancials={showFinancials}
          />
        )}
      </div>

      {/* Inventory Drawer */}
      <InventoryDrawer
        inventoryId={drawer.resourceId}
        open={drawer.isOpen}
        onOpenChange={(open) => !open && drawer.close()}
        onEdit={handleOpenEdit}
        showFinancials={showFinancials}
      />

      {/* Inventory Wizard Modal */}
      <InventoryWizardModal
        isOpen={isWizardOpen || isCreateRoute || isEditRoute}
        onClose={handleCloseWizard}
        inventoryId={editingInventoryId || urlInventoryId}
        onSuccess={handleWizardSuccess}
      />
    </div>
  );
}
